import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import NavigationMenu from '../components/NavigationMenu';
import UserMenu from '../components/UserMenu';
import Footer from '../components/Footer';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useGuest } from '../context/GuestContext';
import { toast } from 'sonner';
import {
  Calendar, List, Plus, Clock, Edit2, Trash2, ExternalLink,
  ChevronLeft, ChevronRight, Camera, Moon, Sun, ShoppingCart,
  Link2, Loader2, ChevronDown, ChevronUp, Tag, Upload, Image, Check, X, Zap
} from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday, addMonths, subMonths, differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';

const AMAZON_AFFILIATE_TAG = 'flipvalue-20';
const STORAGE_KEY = 'flipvalue_releases';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Categories for releases
const CATEGORIES = [
  { value: '', label: 'Select Category (optional)' },
  { value: 'sneakers', label: '👟 Sneakers' },
  { value: 'pokemon', label: '🎴 Pokemon TCG' },
  { value: 'sports-cards', label: '⚾ Sports Cards' },
  { value: 'funko', label: '🎁 Funko Pop' },
  { value: 'electronics', label: '📱 Electronics' },
  { value: 'collectibles', label: '🏆 Collectibles' },
  { value: 'clothing', label: '👕 Clothing' },
  { value: 'other', label: '📦 Other' }
];

// Check if URL is a direct Amazon product URL
const isDirectAmazonUrl = (url) => {
  if (!url) return false;
  return url.includes('amazon.com') && (url.includes('/dp/') || url.includes('/gp/product/'));
};

// Convert Amazon URL to affiliate link
const convertToAffiliateUrl = (url) => {
  if (!url) return null;
  
  try {
    const urlObj = new URL(url);
    
    // Remove existing tag if present
    urlObj.searchParams.delete('tag');
    
    // Add affiliate tag
    urlObj.searchParams.set('tag', AMAZON_AFFILIATE_TAG);
    
    return urlObj.toString();
  } catch {
    // If URL parsing fails, append tag manually
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}tag=${AMAZON_AFFILIATE_TAG}`;
  }
};

// Clean and enhance search keywords for Amazon
const cleanSearchKeywords = (release) => {
  let keywords = release.name || '';
  
  // Add notes if present (but not if it's a URL)
  if (release.notes && !release.notes.startsWith('http')) {
    keywords += ' ' + release.notes;
  }
  
  // Category-specific keyword enhancements
  const category = (release.category || '').toLowerCase();
  
  if (category === 'pokemon' || keywords.toLowerCase().includes('pokemon')) {
    // Add common Pokemon TCG modifiers
    if (!keywords.toLowerCase().includes('booster') && !keywords.toLowerCase().includes('etb') && !keywords.toLowerCase().includes('elite trainer')) {
      // Check if it might be a booster box or ETB
      if (keywords.toLowerCase().includes('box')) {
        keywords += ' sealed';
      }
    }
    keywords += ' pre-order';
  } else if (category === 'sneakers' || keywords.toLowerCase().match(/nike|jordan|adidas|yeezy|dunk/i)) {
    keywords += ' authentic';
  } else if (category === 'funko' || keywords.toLowerCase().includes('funko')) {
    keywords += ' vinyl figure';
  }
  
  // Clean up excess whitespace
  return keywords.replace(/\s+/g, ' ').trim();
};

// Generate smart Amazon URL
const getAmazonUrl = (release) => {
  // Check for direct Amazon URL first
  if (release.amazonUrl && isDirectAmazonUrl(release.amazonUrl)) {
    return convertToAffiliateUrl(release.amazonUrl);
  }
  
  // Check if notes contains Amazon URL
  if (release.notes && isDirectAmazonUrl(release.notes)) {
    return convertToAffiliateUrl(release.notes);
  }
  
  // Build search URL with cleaned keywords
  const searchTerms = cleanSearchKeywords(release);
  const encodedSearch = encodeURIComponent(searchTerms);
  return `https://www.amazon.com/s?k=${encodedSearch}&tag=${AMAZON_AFFILIATE_TAG}`;
};

// Get Amazon button text
const getAmazonButtonText = (release) => {
  if ((release.amazonUrl && isDirectAmazonUrl(release.amazonUrl)) || 
      (release.notes && isDirectAmazonUrl(release.notes))) {
    return 'Pre-Order on Amazon';
  }
  return 'Search/Pre-Order on Amazon';
};

export default function ReleaseSchedulePage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const { promptForInventory, addToLocalReleases } = useGuest();
  const [releases, setReleases] = useState([]);
  const [viewMode, setViewMode] = useState('calendar');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  
  // Check if user is Pro (only Pro can add releases)
  const isProUser = user?.subscription?.tier === 'pro' || user?.subscription?.tier === 'lifetime_pro';
  const canAddRelease = isProUser;
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    time: '',
    notes: '',
    category: '',
    amazonUrl: '',
    retailPrice: '',
    reminders: []
  });
  
  // Quick Add state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddUrl, setQuickAddUrl] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [fetchSuccess, setFetchSuccess] = useState('');
  
  // Screenshot upload state
  const [showScreenshotUpload, setShowScreenshotUpload] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  // Load releases from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      setReleases(JSON.parse(saved));
    }
  }, []);

  // Save releases to localStorage
  const saveReleases = (newReleases) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newReleases));
    setReleases(newReleases);
  };

  // Request notification permission
  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  };

  // Schedule notification
  const scheduleNotification = (release, reminderMinutes) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const releaseTime = new Date(`${release.date}T${release.time || '00:00'}`);
      const notifyTime = new Date(releaseTime.getTime() - reminderMinutes * 60 * 1000);
      const now = new Date();
      
      if (notifyTime > now) {
        const timeout = notifyTime.getTime() - now.getTime();
        setTimeout(() => {
          new Notification('FlipValue Release Reminder', {
            body: `${release.name} releases ${reminderMinutes >= 60 ? `in ${reminderMinutes / 60} hour(s)` : `in ${reminderMinutes} minutes`}!`,
            icon: '/favicon.ico'
          });
        }, timeout);
      }
    }
  };

  // Check if URL is valid for fetching (excludes Amazon)
  const isValidUrl = (url) => {
    if (!url || !url.trim()) return false;
    const trimmed = url.trim().toLowerCase();
    return (
      trimmed.includes('soleretriever.com') ||
      trimmed.includes('sneakernews.com') ||
      trimmed.includes('nicekicks.com') ||
      trimmed.includes('hypebeast.com') ||
      trimmed.includes('pokemon.com') ||
      trimmed.includes('tcg.pokemon.com')
    );
  };

  // Check if URL is Amazon
  const isAmazonUrl = (url) => {
    if (!url || !url.trim()) return false;
    const trimmed = url.trim().toLowerCase();
    return trimmed.includes('amazon.com') || trimmed.includes('a.co/') || trimmed.includes('amzn.to/');
  };

  // Convert Amazon URL to affiliate link
  const convertToAffiliateUrl = (url) => {
    if (!url) return url;
    let cleanUrl = url.trim();
    // Remove existing tag parameter
    cleanUrl = cleanUrl.replace(/[?&]tag=[^&]*/g, '');
    cleanUrl = cleanUrl.replace(/[?&]$/g, '');
    // Add affiliate tag
    const separator = cleanUrl.includes('?') ? '&' : '?';
    return `${cleanUrl}${separator}tag=flipvalue-20`;
  };

  // Handle Amazon URL paste in Direct Amazon URL field
  const handleAmazonUrlChange = (e) => {
    let url = e.target.value;
    // Auto-convert to affiliate URL if it looks like Amazon
    if (url && isAmazonUrl(url)) {
      url = convertToAffiliateUrl(url);
    }
    setFormData(prev => ({ ...prev, amazonUrl: url }));
  };

  // Fetch release details from URL
  const handleFetchUrl = async (urlOverride = null) => {
    const urlToFetch = urlOverride || quickAddUrl;
    if (!urlToFetch?.trim()) return;
    
    // Check if Amazon URL - show friendly message instead
    if (isAmazonUrl(urlToFetch)) {
      setFetchError("Amazon not supported for auto-fetch due to frequent blocking. Paste the URL in 'Product URL' field above for direct purchase button.");
      return;
    }
    
    // Prevent double-fetch
    if (isFetching) return;
    
    setIsFetching(true);
    setFetchError('');
    setFetchSuccess('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/releases/fetch-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: urlToFetch.trim() })
      });
      
      const data = await response.json();
      
      // Build list of extracted fields for display
      const extractedFields = data.detected_fields || [];
      const displayFields = [];
      if (data.title) displayFields.push('title');
      if (data.price) displayFields.push('price');
      if (data.date) displayFields.push('date');
      if (data.notes) displayFields.push('notes');
      if (data.amazon_url) displayFields.push('link');
      
      // Always auto-fill whatever data we got
      setFormData(prev => ({
        ...prev,
        name: data.title || prev.name,
        date: data.date || prev.date,
        notes: data.notes || prev.notes,
        retailPrice: data.price?.toString() || prev.retailPrice,
        amazonUrl: data.amazon_url || prev.amazonUrl
      }));
      
      if (data.success) {
        // Full or partial success
        const hasAllMain = data.title && data.price && data.date;
        const hasTitle = !!data.title;
        
        if (hasAllMain) {
          // Full success - green check
          setFetchSuccess(`✓ Fetched title, price, and date from Amazon`);
        } else if (hasTitle) {
          // Partial success - got title at least
          const missing = [];
          if (!data.price) missing.push('price');
          if (!data.date) missing.push('date');
          setFetchSuccess(`✓ Fetched title from Amazon – enter ${missing.join(' and ')} manually`);
        } else {
          // Very partial
          setFetchSuccess(`⚠️ Partial data – direct pre-order link ready`);
        }
        
        setShowQuickAdd(false);
        setQuickAddUrl('');
      } else {
        setFetchError(data.error || 'Could not fetch release details. Enter manually above.');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setFetchError('Failed to fetch. Check the URL and try again.');
    } finally {
      setIsFetching(false);
    }
  };

  // Auto-fetch on paste for valid URLs (excludes Amazon)
  const handleUrlPaste = (e) => {
    const pastedUrl = e.clipboardData?.getData('text') || '';
    setQuickAddUrl(pastedUrl);
    
    // Check if Amazon URL - show message but don't auto-fetch
    if (isAmazonUrl(pastedUrl)) {
      setFetchError("Amazon not supported for auto-fetch. Paste in 'Product URL' field above instead.");
      return;
    }
    
    // Auto-fetch for supported sites
    if (isValidUrl(pastedUrl)) {
      setTimeout(() => handleFetchUrl(pastedUrl), 100);
    }
  };

  // Handle URL input change
  const handleUrlChange = (e) => {
    const newUrl = e.target.value;
    setQuickAddUrl(newUrl);
    setFetchError('');
  };

  // Handle screenshot file selection (preview only)
  const handleScreenshotSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setFetchError('Please upload an image file (PNG, JPG, or WEBP).');
      return;
    }
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setFetchError('Image too large. Please use an image under 10MB.');
      return;
    }
    
    setFetchError('');
    setExtractedData(null);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setScreenshotPreview(event.target?.result);
    };
    reader.readAsDataURL(file);
  };

  // Analyze screenshot with AI
  const handleAnalyzeScreenshot = async () => {
    if (!screenshotPreview) return;
    if (isExtracting) return;
    
    setIsExtracting(true);
    setFetchError('');
    setExtractedData(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/releases/extract-screenshot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_base64: screenshotPreview })
      });
      
      const data = await response.json();
      
      if (data.success && (data.title || data.price || data.date)) {
        setExtractedData(data);
      } else if (data.success) {
        setFetchError('No release data found in image. Try a clearer screenshot or enter manually.');
      } else {
        setFetchError(data.error || 'Could not analyze screenshot. Try again or enter manually.');
      }
    } catch (err) {
      console.error('Screenshot analysis error:', err);
      setFetchError('Failed to analyze screenshot. Check your connection and try again.');
    } finally {
      setIsExtracting(false);
    }
  };

  // Re-analyze screenshot
  const handleReanalyze = () => {
    handleAnalyzeScreenshot();
  };

  // Apply extracted data to form
  const applyExtractedData = () => {
    if (!extractedData) return;
    
    // Count extracted fields for feedback
    const extractedFields = [];
    if (extractedData.title) extractedFields.push('Title');
    if (extractedData.price) extractedFields.push('Price');
    if (extractedData.date) extractedFields.push('Date');
    if (extractedData.time) extractedFields.push('Time');
    if (extractedData.notes) extractedFields.push('Notes');
    
    setFormData(prev => ({
      ...prev,
      name: extractedData.title || prev.name,
      date: extractedData.date || prev.date,
      time: extractedData.time || prev.time,
      notes: extractedData.notes || prev.notes,
      retailPrice: extractedData.price?.toString() || prev.retailPrice
    }));
    
    setExtractedData(null);
    setScreenshotPreview(null);
    setShowScreenshotUpload(false);
    setFetchSuccess(`✓ Applied from screenshot: ${extractedFields.join(', ')}`);
  };

  // Cancel screenshot extraction
  const cancelExtraction = () => {
    setExtractedData(null);
    setScreenshotPreview(null);
    setIsExtracting(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newRelease = {
      id: editingRelease?.id || Date.now().toString(),
      name: formData.name,
      date: formData.date,
      time: formData.time || '00:00',
      notes: formData.notes,
      category: formData.category,
      amazonUrl: formData.amazonUrl,
      retailPrice: formData.retailPrice ? parseFloat(formData.retailPrice) : null,
      reminders: formData.reminders,
      createdAt: editingRelease?.createdAt || new Date().toISOString()
    };

    // If guest and adding new release (not editing), show prompt
    if (!user && !editingRelease) {
      promptForInventory(() => {
        addToLocalReleases(newRelease);
        let newReleases = [...releases, newRelease];
        saveReleases(newReleases);
        
        if (formData.reminders.length > 0) {
          requestNotificationPermission();
          formData.reminders.forEach(mins => scheduleNotification(newRelease, mins));
        }
        
        resetForm();
      });
      return;
    }

    let newReleases;
    if (editingRelease) {
      newReleases = releases.map(r => r.id === editingRelease.id ? newRelease : r);
    } else {
      newReleases = [...releases, newRelease];
    }

    saveReleases(newReleases);

    if (formData.reminders.length > 0) {
      requestNotificationPermission();
      formData.reminders.forEach(mins => scheduleNotification(newRelease, mins));
    }

    resetForm();
  };

  const resetForm = () => {
    setFormData({ name: '', date: '', time: '', notes: '', category: '', amazonUrl: '', retailPrice: '', reminders: [] });
    setEditingRelease(null);
    setIsAddModalOpen(false);
    setShowQuickAdd(false);
    setShowScreenshotUpload(false);
    setQuickAddUrl('');
    setFetchError('');
    setFetchSuccess('');
    setExtractedData(null);
    setScreenshotPreview(null);
    setIsExtracting(false);
  };

  const handleEdit = (release) => {
    if (!canAddRelease) {
      toast.error('Upgrade to Pro to edit releases');
      navigate('/pricing');
      return;
    }
    setEditingRelease(release);
    setFormData({
      name: release.name,
      date: release.date,
      time: release.time,
      notes: release.notes || '',
      category: release.category || '',
      amazonUrl: release.amazonUrl || '',
      retailPrice: release.retailPrice?.toString() || '',
      reminders: release.reminders || []
    });
    setIsAddModalOpen(true);
  };

  const handleDelete = (id) => {
    if (!canAddRelease) {
      toast.error('Upgrade to Pro to manage releases');
      navigate('/pricing');
      return;
    }
    const newReleases = releases.filter(r => r.id !== id);
    saveReleases(newReleases);
  };

  const toggleReminder = (minutes) => {
    setFormData(prev => ({
      ...prev,
      reminders: prev.reminders.includes(minutes)
        ? prev.reminders.filter(m => m !== minutes)
        : [...prev.reminders, minutes]
    }));
  };

  const getCountdown = (release) => {
    const releaseDate = new Date(`${release.date}T${release.time || '00:00'}`);
    const now = new Date();
    
    if (releaseDate < now) return null;
    
    const days = differenceInDays(releaseDate, now);
    const hours = differenceInHours(releaseDate, now) % 24;
    const minutes = differenceInMinutes(releaseDate, now) % 60;
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const isReleaseToday = (release) => {
    return isSameDay(new Date(release.date), new Date());
  };

  // Calendar helpers
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
  const startPadding = monthStart.getDay();
  const paddedDays = [...Array(startPadding).fill(null), ...calendarDays];

  const getReleasesForDay = (day) => {
    if (!day) return [];
    // Parse date string as local date to avoid timezone issues
    return releases.filter(r => {
      const [year, month, dayNum] = r.date.split('-').map(Number);
      const releaseDate = new Date(year, month - 1, dayNum);
      return isSameDay(releaseDate, day);
    });
  };

  const upcomingReleases = releases
    .filter(r => new Date(`${r.date}T${r.time}`) >= new Date())
    .sort((a, b) => new Date(`${a.date}T${a.time}`) - new Date(`${b.date}T${b.time}`));

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="w-full px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <NavigationMenu />
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <Zap className="w-4 h-4 text-primary-foreground" />
              </div>
              <div className="text-left">
                <h1 className="text-base font-bold leading-tight">FlipValue</h1>
                <p className="text-[10px] text-muted-foreground -mt-0.5">Release Schedule</p>
              </div>
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-secondary">
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="w-full px-4 py-4 flex-1">
        {/* View Toggle & Add Button */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex bg-secondary rounded-lg p-1">
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'calendar' ? 'bg-background shadow' : ''
              }`}
            >
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Calendar</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'list' ? 'bg-background shadow' : ''
              }`}
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">List</span>
            </button>
          </div>
          {canAddRelease ? (
            <Button onClick={() => setIsAddModalOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Add Release
            </Button>
          ) : (
            <Button 
              onClick={() => navigate('/pricing')} 
              variant="outline" 
              className="gap-2 text-muted-foreground"
            >
              <Plus className="w-4 h-4" />
              Add Release
              <span className="text-[9px] px-1.5 py-0.5 bg-primary/20 text-primary rounded ml-1">PRO</span>
            </Button>
          )}
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <div className="bg-card rounded-2xl border border-border p-4">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="p-2 hover:bg-secondary rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-lg font-bold">
                {format(currentMonth, 'MMMM yyyy')}
              </h2>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="p-2 hover:bg-secondary rounded-lg"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {paddedDays.map((day, idx) => {
                const dayReleases = getReleasesForDay(day);
                const isCurrentDay = day && isToday(day);
                
                return (
                  <div
                    key={idx}
                    className={`min-h-[80px] p-1 rounded-lg border ${
                      !day ? 'bg-transparent border-transparent' :
                      isCurrentDay ? 'bg-primary/10 border-primary' :
                      'border-border hover:bg-secondary/50'
                    }`}
                  >
                    {day && (
                      <>
                        <span className={`text-sm font-medium ${isCurrentDay ? 'text-primary' : ''}`}>
                          {format(day, 'd')}
                        </span>
                        <div className="mt-1 space-y-1">
                          {dayReleases.slice(0, 2).map(release => (
                            <div
                              key={release.id}
                              onClick={() => handleEdit(release)}
                              className="text-xs bg-primary text-primary-foreground px-1 py-0.5 rounded truncate cursor-pointer"
                            >
                              {release.name}
                            </div>
                          ))}
                          {dayReleases.length > 2 && (
                            <div className="text-xs text-muted-foreground">
                              +{dayReleases.length - 2} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="space-y-3">
            {upcomingReleases.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No upcoming releases</p>
                <p className="text-sm">Add your first release to get started</p>
              </div>
            ) : (
              upcomingReleases.map(release => {
                const countdown = getCountdown(release);
                const today = isReleaseToday(release);
                const hasDirectAmazon = (release.amazonUrl && isDirectAmazonUrl(release.amazonUrl)) || 
                                        (release.notes && isDirectAmazonUrl(release.notes));
                
                return (
                  <div
                    key={release.id}
                    className={`bg-card rounded-xl border p-4 ${
                      today ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold truncate">{release.name}</h3>
                          {today && (
                            <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                              TODAY
                            </span>
                          )}
                          {release.category && (
                            <span className="px-2 py-0.5 bg-secondary text-xs rounded-full">
                              {CATEGORIES.find(c => c.value === release.category)?.label || release.category}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {(() => {
                            // Parse date as local to avoid timezone issues
                            const [year, month, day] = release.date.split('-').map(Number);
                            const localDate = new Date(year, month - 1, day);
                            return format(localDate, 'EEE, MMM d, yyyy');
                          })()} at {release.time}
                        </p>
                        {release.retailPrice && (
                          <p className="text-sm mt-1">
                            Retail: <span className="font-mono font-semibold">${release.retailPrice}</span>
                          </p>
                        )}
                        {release.notes && !isDirectAmazonUrl(release.notes) && (
                          <p className="text-sm text-muted-foreground mt-1 truncate">{release.notes}</p>
                        )}
                        {release.amazonUrl && (
                          <a 
                            href={release.amazonUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline mt-2 flex items-center gap-1.5 truncate font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Link2 className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{release.amazonUrl}</span>
                          </a>
                        )}
                      </div>
                      
                      <div className="flex flex-col items-end gap-2">
                        {countdown && (
                          <div className="flex items-center gap-1 text-primary font-mono font-bold">
                            <Clock className="w-4 h-4" />
                            {countdown}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 mt-3 pt-3 border-t border-border">
                      {/* Amazon Affiliate Button */}
                      <Button
                        size="sm"
                        onClick={() => window.open(getAmazonUrl(release), '_blank')}
                        className={`w-full gap-2 ${
                          hasDirectAmazon 
                            ? 'bg-orange-500 hover:bg-orange-600' 
                            : 'bg-green-600 hover:bg-green-700'
                        } text-white`}
                      >
                        <ShoppingCart className="w-4 h-4" />
                        {getAmazonButtonText(release)}
                        {hasDirectAmazon && <Tag className="w-3 h-3" />}
                      </Button>
                      {/* Accuracy note */}
                      <p className="text-[10px] text-muted-foreground text-center -mt-1">
                        For best accuracy, manually paste the exact Amazon product URL.
                      </p>
                      
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => handleEdit(release)}
                          className="p-2 hover:bg-secondary rounded-lg"
                          title="Edit release"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(release.id)}
                          className="p-2 hover:bg-destructive/10 text-destructive rounded-lg"
                          title="Delete release"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Affiliate Disclaimer */}
        {releases.length > 0 && (
          <p className="text-xs text-muted-foreground text-center mt-6 px-4">
            (Affiliate links – we may earn a commission on qualifying Amazon purchases at no extra cost to you)
          </p>
        )}
      </main>

      {/* Add/Edit Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={(open) => !open && resetForm()}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRelease ? 'Edit Release' : 'Add New Release'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Primary Manual Fields */}
            <div>
              <label className="text-sm font-medium">Item Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Nike Dunk Low Panda"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Release Date *</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium">Time</label>
                <Input
                  type="time"
                  value={formData.time}
                  onChange={(e) => setFormData(prev => ({ ...prev, time: e.target.value }))}
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Expected Retail Price</label>
              <Input
                type="number"
                step="0.01"
                value={formData.retailPrice}
                onChange={(e) => setFormData(prev => ({ ...prev, retailPrice: e.target.value }))}
                placeholder="e.g., 110.00"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Notes</label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="e.g., Elite Trainer Box, Limited Edition"
              />
            </div>

            <div>
              <label className="text-sm font-medium">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            {/* Product URL Field */}
            <div>
              <label className="text-sm font-medium flex items-center gap-1">
                <Link2 className="w-3 h-3 text-blue-500" />
                Product URL (optional)
              </label>
              <Input
                value={formData.amazonUrl}
                onChange={handleAmazonUrlChange}
                placeholder="e.g., https://nike.com/... or amazon.com/dp/..."
                className={formData.amazonUrl && isAmazonUrl(formData.amazonUrl) ? 'border-orange-300 focus:border-orange-500' : ''}
              />
              <p className="text-xs text-muted-foreground mt-1">
                💡 Adding a URL improves accuracy & enables direct purchase button
              </p>
              {formData.amazonUrl && formData.amazonUrl.includes('tag=flipvalue-20') && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Amazon affiliate link ready
                </p>
              )}
            </div>

            {/* ===== SECTION 2: Screenshot Upload (Recommended) ===== */}
            {!editingRelease && (
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-xl opacity-50 blur-sm" />
                <div className="relative bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-cyan-500/10 border-2 border-emerald-400/50 rounded-xl p-4">
                  <button
                    type="button"
                    onClick={() => { setShowScreenshotUpload(!showScreenshotUpload); setShowQuickAdd(false); }}
                    className="flex items-center justify-between w-full"
                  >
                    <span className="flex items-center gap-2 font-medium text-emerald-600 dark:text-emerald-400">
                      <div className="p-1.5 bg-emerald-500/20 rounded-lg">
                        <Image className="w-4 h-4" />
                      </div>
                      <span>📸 Upload Screenshot</span>
                      <span className="text-xs bg-emerald-500/20 px-2 py-0.5 rounded-full">Recommended</span>
                    </span>
                    <div className={`p-1 rounded-full bg-emerald-500/20 transition-transform ${showScreenshotUpload ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                  </button>
                  
                  {!showScreenshotUpload && (
                    <p className="text-xs text-muted-foreground mt-2 ml-9">
                      AI extracts title, price, date from screenshots
                    </p>
                  )}
                  
                  {showScreenshotUpload && (
                    <div className="mt-4 space-y-3">
                      {!screenshotPreview ? (
                        <>
                          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-emerald-300 dark:border-emerald-600 rounded-lg cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <Upload className="w-8 h-8 mb-2 text-emerald-500" />
                              <p className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                                Click to upload screenshot
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                PNG, JPG, or WEBP (max 10MB)
                              </p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*"
                              onChange={handleScreenshotSelect}
                            />
                          </label>
                          <p className="text-xs text-muted-foreground text-center">
                            Works best with Instagram posts, sneaker calendars, product pages
                          </p>
                        </>
                      ) : (
                        <div className="space-y-3">
                          <div className="relative">
                            <img 
                              src={screenshotPreview} 
                              alt="Screenshot preview" 
                              className="w-full h-36 object-contain bg-black/5 rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={cancelExtraction}
                              className="absolute top-2 right-2 p-1.5 bg-black/60 rounded-full hover:bg-black/80 transition-colors"
                              title="Remove image"
                            >
                              <X className="w-4 h-4 text-white" />
                            </button>
                          </div>
                          
                          {!extractedData && !isExtracting && (
                            <Button
                              type="button"
                              onClick={handleAnalyzeScreenshot}
                              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                            >
                              <Camera className="w-4 h-4" />
                              Analyze Image
                            </Button>
                          )}
                          
                          {isExtracting && (
                            <div className="flex items-center justify-center gap-2 py-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                              <Loader2 className="w-5 h-5 animate-spin text-emerald-600" />
                              <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                                Analyzing screenshot with AI...
                              </span>
                            </div>
                          )}
                          
                          {fetchError && showScreenshotUpload && !extractedData && !isExtracting && (
                            <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                              <p className="text-xs text-amber-700 dark:text-amber-300 flex items-start gap-2">
                                <span>⚠️</span>
                                <span>{fetchError}</span>
                              </p>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={handleReanalyze}
                                className="mt-2 w-full"
                              >
                                Try Again
                              </Button>
                            </div>
                          )}
                          
                          {extractedData && (
                            <div className="bg-white dark:bg-background rounded-lg p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <h4 className="font-medium text-sm flex items-center gap-2">
                                  <Check className="w-4 h-4 text-emerald-500" />
                                  Extracted Data
                                </h4>
                                {extractedData.confidence && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    extractedData.confidence >= 0.8 
                                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' 
                                      : extractedData.confidence >= 0.5 
                                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                  }`}>
                                    {Math.round(extractedData.confidence * 100)}% confident
                                  </span>
                                )}
                              </div>
                              
                              {extractedData.detected_fields && extractedData.detected_fields.length > 0 && (
                                <div className="flex flex-wrap gap-1">
                                  <span className="text-xs text-muted-foreground">Found:</span>
                                  {extractedData.detected_fields.map(field => (
                                    <span key={field} className="text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded capitalize">
                                      {field}
                                    </span>
                                  ))}
                                </div>
                              )}
                              
                              <div className="space-y-1.5 text-sm border-t border-border pt-2">
                                {extractedData.title && (
                                  <p className="flex gap-2">
                                    <span className="text-muted-foreground shrink-0">Title:</span>
                                    <span className="font-medium">{extractedData.title}</span>
                                  </p>
                                )}
                                {extractedData.date && (
                                  <p className="flex gap-2">
                                    <span className="text-muted-foreground shrink-0">Date:</span>
                                    <span>{extractedData.date}</span>
                                  </p>
                                )}
                                {extractedData.time && (
                                  <p className="flex gap-2">
                                    <span className="text-muted-foreground shrink-0">Time:</span>
                                    <span>{extractedData.time}</span>
                                  </p>
                                )}
                                {extractedData.price && (
                                  <p className="flex gap-2">
                                    <span className="text-muted-foreground shrink-0">Price:</span>
                                    <span className="font-mono font-medium">${extractedData.price}</span>
                                  </p>
                                )}
                                {extractedData.notes && (
                                  <p className="flex gap-2">
                                    <span className="text-muted-foreground shrink-0">Notes:</span>
                                    <span>{extractedData.notes}</span>
                                  </p>
                                )}
                              </div>
                              
                              <div className="flex gap-2 pt-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={applyExtractedData}
                                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                                >
                                  <Check className="w-4 h-4 mr-1" />
                                  Use This Data
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={handleReanalyze}
                                  className="px-3"
                                  title="Re-analyze image"
                                >
                                  <Loader2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={cancelExtraction}
                                  className="px-3"
                                  title="Cancel"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ===== SECTION 3: Quick Add from URL (No Amazon) ===== */}
            {!editingRelease && (
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-xl opacity-40 blur-sm" />
                <div className="relative bg-gradient-to-r from-blue-500/10 via-indigo-500/10 to-purple-500/10 border-2 border-blue-400/50 rounded-xl p-4">
                  <button
                    type="button"
                    onClick={() => { setShowQuickAdd(!showQuickAdd); setShowScreenshotUpload(false); }}
                    className="flex items-center justify-between w-full"
                  >
                    <span className="flex items-center gap-2 font-medium text-blue-600 dark:text-blue-400">
                      <div className="p-1.5 bg-blue-500/20 rounded-lg">
                        <Link2 className="w-4 h-4" />
                      </div>
                      <span>🔗 Quick Add from URL</span>
                    </span>
                    <div className={`p-1 rounded-full bg-blue-500/20 transition-transform ${showQuickAdd ? 'rotate-180' : ''}`}>
                      <ChevronDown className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                  </button>
                  
                  {!showQuickAdd && (
                    <p className="text-xs text-muted-foreground mt-2 ml-9">
                      Auto-fill from Sole Retriever, Sneaker News, Nice Kicks, Hypebeast, Pokemon TCG
                    </p>
                  )}
                  
                  {showQuickAdd && (
                    <div className="mt-4 space-y-3">
                      <div className="flex flex-wrap gap-1.5">
                        {['Sole Retriever', 'Sneaker News', 'Nice Kicks', 'Hypebeast', 'Pokemon TCG'].map(site => (
                          <span key={site} className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/50 dark:bg-white/10">
                            {site}
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={quickAddUrl}
                          onChange={handleUrlChange}
                          onPaste={handleUrlPaste}
                          placeholder="Paste URL here (auto-fetches on paste)"
                          className="flex-1 bg-white dark:bg-background"
                          disabled={isFetching}
                        />
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleFetchUrl()}
                          disabled={isFetching || !quickAddUrl.trim()}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 min-w-[80px]"
                        >
                          {isFetching ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin mr-1" />
                              <span className="hidden sm:inline">Fetching...</span>
                            </>
                          ) : (
                            <span>Fetch</span>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        💡 Paste a URL to auto-fetch release details
                      </p>
                      {fetchError && showQuickAdd && (
                        <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-2 rounded flex items-start gap-2">
                          <span>⚠️</span>
                          <span>{fetchError}</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Success/Error Messages (outside collapsed sections) */}
            {fetchError && !showQuickAdd && !showScreenshotUpload && (
              <p className="text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-2 rounded">{fetchError}</p>
            )}
            {fetchSuccess && (
              <p className="text-xs text-green-600 bg-green-50 dark:bg-green-900/20 p-2 rounded">{fetchSuccess}</p>
            )}

            {/* Reminders */}
            <div>
              <label className="text-sm font-medium mb-2 block">Reminders</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: '1 week', minutes: 10080 },
                  { label: '24 hours', minutes: 1440 },
                  { label: '1 hour', minutes: 60 },
                  { label: '15 min', minutes: 15 }
                ].map(option => (
                  <button
                    key={option.minutes}
                    type="button"
                    onClick={() => toggleReminder(option.minutes)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      formData.reminders.includes(option.minutes)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-border hover:border-primary'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={resetForm} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                {editingRelease ? 'Save Changes' : 'Add Release'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Footer */}
      <Footer />
    </div>
  );
}
